import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { AbstractControl, ControlContainer } from '@angular/forms';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@Directive()
export abstract class ControlEnableBase implements AfterViewInit, OnDestroy {
  abstract get control(): AbstractControl | null | undefined;

  private disabled = false;

  private _subscriptions: Subscription[] = [];

  constructor(
    private renderer: Renderer2,
    private el: ElementRef<HTMLInputElement>,
  ) {}

  ngAfterViewInit() {
    if (!this.control) {
      environment.warn('No control for ControlEnable');
    }

    this.setChecked();

    if (this.control) {
      const root = this.control.root !== this.control ? this.control.root : null;

      this._subscriptions.push(
        this.control.statusChanges
          .pipe(filter(() => (root?.enabled ?? true) && this.control?.disabled !== this.disabled))
          .subscribe(() => this.setChecked()),
      );

      if (root) {
        this._subscriptions.push(
          root.statusChanges
            .pipe(
              map(() => root?.enabled ?? true),
              distinctUntilChanged(),
            )
            .subscribe(enabled => {
              if (enabled) {
                this.renderer.setProperty(this.el.nativeElement, 'disabled', false);
              } else {
                this.renderer.setProperty(this.el.nativeElement, 'disabled', true);
              }
            }),
        );
      }
    }
  }

  private setChecked() {
    if (this.control?.disabled) {
      this.disabled = true;
      this.renderer.setProperty(this.el.nativeElement, 'checked', false);
    } else {
      this.disabled = false;
      this.renderer.setProperty(this.el.nativeElement, 'checked', true);
    }
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  @HostListener('change', ['$event.target.checked'])
  onChange(checked: boolean) {
    if (!this.control) {
      environment.warn('No control for ControlEnable');
      return;
    }

    if (checked) {
      if (this.control.disabled) {
        this.disabled = false;
        this.control.enable();
        this.control.parent?.markAsDirty();
        this.control.parent?.markAsTouched();
      }
    } else {
      if (this.control.enabled) {
        this.disabled = true;
        this.control.parent?.markAsDirty();
        this.control.parent?.markAsTouched();
        this.control.disable();
      }
    }
  }
}

@Directive({
  selector: 'input[type="checkbox"][appControlEnable]:not([control]):not([controlName])',
  standalone: false,
})
export class ControlEnableContainerDirective extends ControlEnableBase {
  get control() {
    return this.controlContainer.control;
  }

  @HostBinding()
  checked = false;

  constructor(
    renderer: Renderer2,
    el: ElementRef<HTMLInputElement>,
    @Host() private controlContainer: ControlContainer,
  ) {
    super(renderer, el);
  }
}

@Directive({
  selector: 'input[type="checkbox"][appControlEnable][control]',
  standalone: false,
})
export class ControlEnableControlDirective extends ControlEnableBase {
  @Input() control: AbstractControl;

  @HostBinding()
  checked = false;
}

@Directive({
  selector: 'input[type="checkbox"][appControlEnable][controlName]',
  standalone: false,
})
export class ControlEnableControlNameDirective extends ControlEnableBase {
  @Input() controlName: string;

  get control() {
    return this.controlContainer.control?.get(this.controlName);
  }

  @HostBinding()
  checked = false;

  constructor(
    renderer: Renderer2,
    el: ElementRef<HTMLInputElement>,
    @Inject(ControlContainer) private controlContainer: ControlContainer,
  ) {
    super(renderer, el);
  }
}
